import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  ContentCopy as CopyIcon,
  OpenInNew as OpenIcon,
  InsertDriveFileOutlined as FileIcon,
  CloudUploadOutlined as UploadIcon,
  VisibilityOutlined as ViewsIcon,
  LocalOfferOutlined as TagIcon,
} from "@mui/icons-material";
import ResizablePanel from "../../components/common/ResizablePanel";
import { AddressbookPanelShell } from "../../components/component-contact";
import { contactCardSx } from "../../components/component-contact/ContactCard";
import { apiGetFiles, apiGetFileSignedUrl, apiCreateFile } from "../../graphQL/file";
import { apiGetBlocks, apiCreateBlock } from "../../graphQL/blocks";
import { apiGetUser } from "../../graphQL/user";
import { useUploadContext } from "../../Provider/UploadContext";
import { v4 as uuid_v4 } from "../../libs/uuid";
import config from "../../config";
import { buildTrackedFileUrl } from "../../components/files/utils/shareUrl";

// This menu shows ONLY system files and public files (never contact/property
// files). System = isSystem:true; Public = isPublic:true. Both are queried
// independently and merged.
const SCOPES = {
  system: { label: "System", bg: "#EDE9FE", fg: "#5B21B6" },
  public: { label: "Public", bg: "#DCFCE7", fg: "#166534" },
};
const scopeOf = (f) => (f.isSystem ? "system" : "public");

const fileName = (f) =>
  (f.description && f.description.trim()) ||
  (f.key ? String(f.key).split("/").pop() : "") ||
  "Untitled file";

const fmtSize = (bytes) => {
  const n = Number(bytes) || 0;
  if (n <= 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

const fmtTime = (ts) => {
  const n = typeof ts === "number" ? ts : Number(ts) || Date.parse(ts);
  if (!n || Number.isNaN(n)) return "";
  return new Date(n).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
};

const publicUrlOf = (f) => f.publicUrl || f.url || "";

/* ─────────────────── File-access analytics (blocks) ───────────────────
   Every in-app access is logged as a block: schemaType "fileAccess",
   parentId = file id, type = view | open | copy, authorId = who,
   data = { tz, locale, ua } (where, as reported by the browser).
   Direct hits on a tracked public link (/api/f/<id>) are logged by the
   backend as type "direct" with data.geo (CloudFront viewer-geo),
   data.referrer, and data.campaign (utm/campaign params). */
const SCHEMA_ACCESS = "fileAccess";
const ACTION_META = {
  view: { label: "viewed", bg: "#EEF2FF", fg: "#3730A3" },
  open: { label: "opened", bg: "#DCFCE7", fg: "#166534" },
  copy: { label: "copied URL", bg: "#FEF3C7", fg: "#92400E" },
  // Direct public-link hit logged by the backend redirect endpoint.
  direct: { label: "direct hit", bg: "#E8EAF6", fg: "#283593" },
};
const actionMeta = (t) => ACTION_META[t] || { label: t || "accessed", bg: "#E2E8F0", fg: "#1F2937" };

// Human-readable "where" for a direct hit, from CloudFront viewer-geo.
const directWhere = (data) => {
  const city = String(data?.geo?.city || "").trim();
  const country = String(data?.geo?.country || "").trim();
  return [city, country].filter(Boolean).join(", ");
};

// Short referrer host for display (drops scheme + path).
const referrerHost = (ref) => {
  if (!ref) return "";
  try { return new URL(ref).hostname.replace(/^www\./, ""); } catch { return String(ref); }
};

const fmtWhen = (ts) => {
  const n = typeof ts === "number" ? ts : Number(ts) || Date.parse(ts);
  if (!n || Number.isNaN(n)) return "";
  return new Date(n).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
};

// authorId -> display name, cached across renders.
const authorCache = new Map();
const displayFromUser = (u, id) => {
  const n = String(u?.name || u?.username || "").trim();
  if (n.includes("@")) return n.split("@")[0];
  return n || (id ? String(id).slice(0, 8) : "Unknown");
};
const useAuthorNames = (ids) => {
  const [, force] = useState(0);
  const key = ids.filter(Boolean).join(",");
  useEffect(() => {
    const missing = [...new Set(ids)].filter((id) => id && !authorCache.has(id));
    if (!missing.length) return undefined;
    let alive = true;
    Promise.all(missing.map(async (id) => {
      try { authorCache.set(id, displayFromUser(await apiGetUser(id), id)); }
      catch { authorCache.set(id, String(id).slice(0, 8)); }
    })).then(() => { if (alive) force((n) => n + 1); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return (id) => authorCache.get(id) || (id ? String(id).slice(0, 8) : "Unknown");
};

/* ─────────────────────────── List card ─────────────────────────── */

const FileCard = ({ file, selected, onSelect, onCopyUrl, accessCount = 0 }) => {
  const scope = scopeOf(file);
  const sm = SCOPES[scope];
  const category = file.fileCategory || file.type || file.mimeType || "File";
  const canCopy = file.isPublic && publicUrlOf(file);
  return (
    <Box sx={contactCardSx.card(selected, undefined, scope === "system" ? "#7C3AED" : "#16A34A")} onClick={() => onSelect(file)}>
      <Box sx={contactCardSx.cardHeader}>
        <Typography sx={contactCardSx.cardName}>{fileName(file)}</Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
          {accessCount > 0 && (
            <Tooltip title={`Accessed ${accessCount} time${accessCount === 1 ? "" : "s"}`}>
              <Box component="span" sx={{ ...contactCardSx.statusChip("#EEF2FF", "#3730A3"), display: "inline-flex", alignItems: "center", gap: 0.4 }}>
                <ViewsIcon sx={{ fontSize: 11 }} />{accessCount}
              </Box>
            </Tooltip>
          )}
          <Box component="span" sx={contactCardSx.statusChip(sm.bg, sm.fg)}>{sm.label}</Box>
        </Box>
      </Box>
      <Typography sx={{ ...contactCardSx.cardTitle, color: "#6B7280" }}>{category}</Typography>
      <Box sx={contactCardSx.cardFooter}>
        <Typography sx={{ fontSize: 11, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {[fmtSize(file.size), fmtTime(file.updatedAt || file.uploadedAt || file.createdAt)].filter(Boolean).join(" · ")}
        </Typography>
        {canCopy ? (
          <Tooltip title="Copy public URL">
            <IconButton
              size="small"
              sx={contactCardSx.iconBtn}
              onClick={(e) => { e.stopPropagation(); onCopyUrl(file); }}
              aria-label="Copy public URL"
            >
              <CopyIcon sx={{ fontSize: 15 }} />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
    </Box>
  );
};

/* ─────────────────────────── Preview ─────────────────────────── */

const FilePreview = ({ file, authorId }) => {
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    setUrl(""); setFailed(false); setLoading(true);
    const resolve = async () => {
      try {
        if (file.isPublic && publicUrlOf(file)) {
          if (alive) { setUrl(publicUrlOf(file)); setLoading(false); }
          return;
        }
        const signed = await apiGetFileSignedUrl(file.id, authorId);
        if (alive) { setUrl(signed || ""); setLoading(false); }
      } catch (err) {
        console.error("Failed to resolve file url:", err);
        if (alive) { setFailed(true); setLoading(false); }
      }
    };
    resolve();
    return () => { alive = false; };
  }, [file.id, file.isPublic, authorId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress size={22} /></Box>;
  }
  if (failed || !url) {
    return <Typography sx={{ fontSize: 13, color: "text.secondary", py: 2 }}>Preview unavailable.</Typography>;
  }

  const mime = String(file.mimeType || "");
  const frameSx = { width: "100%", height: 460, border: "1px solid #E5E7EB", borderRadius: 8, backgroundColor: "#F8FAFC" };

  if (mime.startsWith("image/")) {
    return <Box component="img" src={url} alt={fileName(file)} sx={{ maxWidth: "100%", maxHeight: 460, borderRadius: 1, border: "1px solid #E5E7EB", display: "block" }} />;
  }
  if (mime === "application/pdf" || mime.startsWith("text/") || mime.includes("html")) {
    return <Box component="iframe" title={fileName(file)} src={url} sx={frameSx} />;
  }
  return (
    <Box sx={{ py: 3, textAlign: "center" }}>
      <FileIcon sx={{ fontSize: 42, color: "#94A3B8" }} />
      <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 1 }}>
        No inline preview for this file type.
      </Typography>
      <Box component="a" href={url} target="_blank" rel="noreferrer" sx={{ fontSize: 13, color: "#2563EB", fontWeight: 600 }}>
        Open file
      </Box>
    </Box>
  );
};

/* ─────────────────────────── Detail panel ─────────────────────────── */

const MetaRow = ({ label, value }) => (
  value ? (
    <Box sx={{ display: "flex", gap: 1, fontSize: 12, py: 0.25 }}>
      <Typography sx={{ fontSize: 12, color: "text.secondary", minWidth: 90 }}>{label}</Typography>
      <Typography sx={{ fontSize: 12, color: "#1F2937", wordBreak: "break-all" }}>{value}</Typography>
    </Box>
  ) : null
);

const FileDetailPanel = ({ file, authorId, onCopyUrl, onCopyText, onOpen, accessList = [] }) => {
  const scope = scopeOf(file);
  const sm = SCOPES[scope];
  const pub = publicUrlOf(file);
  const canCopy = file.isPublic && pub;
  const nameOf = useAuthorNames(accessList.map((a) => a.authorId));
  const [showCampaign, setShowCampaign] = useState(false);
  const [utm, setUtm] = useState({ utm_source: "", utm_medium: "", utm_campaign: "" });

  const trackedUrl = buildTrackedFileUrl(file.id, utm);
  const copyCampaign = () => {
    if (!onCopyText || !trackedUrl) return;
    onCopyText(trackedUrl, "Campaign link copied");
  };
  return (
    <AddressbookPanelShell
      titleContent={
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flex: 1, minWidth: 0 }}>
          <Box component="span" sx={contactCardSx.statusChip(sm.bg, sm.fg)}>{sm.label}</Box>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}>
            {fileName(file)}
          </Typography>
        </Box>
      }
      headerActions={
        <>
          {canCopy && (
            <Tooltip title="Copy public URL">
              <IconButton size="small" onClick={() => onCopyUrl(file)} sx={{ width: 24, height: 24, color: "#64748B", "&:hover": { color: "#16A34A", backgroundColor: "#DCFCE7" } }} aria-label="Copy public URL">
                <CopyIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
          {pub && (
            <Tooltip title="Open in new tab">
              <IconButton size="small" component="a" href={pub} target="_blank" rel="noreferrer" onClick={() => onOpen && onOpen(file)} sx={{ width: 24, height: 24, color: "#64748B", "&:hover": { color: "#3B82F6", backgroundColor: "#EFF6FF" } }} aria-label="Open file">
                <OpenIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          )}
        </>
      }
    >
      {/* metadata on top */}
      <Box sx={{ mb: 1.5 }}>
        <MetaRow label="Name" value={fileName(file)} />
        <MetaRow label="Scope" value={sm.label} />
        <MetaRow label="Category" value={file.fileCategory || file.type} />
        <MetaRow label="Type" value={file.mimeType} />
        <MetaRow label="Size" value={fmtSize(file.size)} />
        <MetaRow label="Uploaded" value={fmtTime(file.uploadedAt || file.createdAt)} />
        <MetaRow label="Updated" value={fmtTime(file.updatedAt)} />
        {canCopy ? (
          <Box sx={{ display: "flex", gap: 1, fontSize: 12, py: 0.25, alignItems: "center" }}>
            <Typography sx={{ fontSize: 12, color: "text.secondary", minWidth: 90 }}>Public URL</Typography>
            <Typography sx={{ fontSize: 12, color: "#2563EB", wordBreak: "break-all", flex: 1 }}>{pub}</Typography>
            <Tooltip title="Copy">
              <IconButton size="small" onClick={() => onCopyUrl(file)} sx={{ width: 22, height: 22, color: "#64748B" }}><CopyIcon sx={{ fontSize: 13 }} /></IconButton>
            </Tooltip>
          </Box>
        ) : null}
        {file.summary ? <MetaRow label="Summary" value={file.summary} /> : null}
      </Box>

      {/* Campaign link: a tracked /api/f/<id> URL that logs the open (geo +
          campaign) then redirects to the file. Single button; the tag icon
          reveals optional utm fields. */}
      {canCopy ? (
        <Box sx={{ mb: 1.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<CopyIcon sx={{ fontSize: 14 }} />}
              onClick={copyCampaign}
              sx={{ textTransform: "none", fontSize: 12, backgroundColor: "#3F51B5", "&:hover": { backgroundColor: "#3949AB" } }}
            >
              Copy campaign link
            </Button>
            <Tooltip title="Campaign tags">
              <IconButton size="small" onClick={() => setShowCampaign((v) => !v)} sx={{ width: 26, height: 26, color: showCampaign ? "#3F51B5" : "#94A3B8", "&:hover": { color: "#3F51B5", backgroundColor: "#E8EAF6" } }} aria-label="Campaign tags">
                <TagIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
          {showCampaign ? (
            <Stack spacing={1} sx={{ mt: 1 }}>
              <Stack direction="row" spacing={1}>
                <TextField label="utm_source" value={utm.utm_source} onChange={(e) => setUtm((u) => ({ ...u, utm_source: e.target.value }))} size="small" fullWidth />
                <TextField label="utm_medium" value={utm.utm_medium} onChange={(e) => setUtm((u) => ({ ...u, utm_medium: e.target.value }))} size="small" fullWidth />
              </Stack>
              <TextField label="utm_campaign" value={utm.utm_campaign} onChange={(e) => setUtm((u) => ({ ...u, utm_campaign: e.target.value }))} size="small" fullWidth />
            </Stack>
          ) : null}
        </Box>
      ) : null}

      {/* preview + access history side by side */}
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", flexWrap: "wrap" }}>
        <Box sx={{ flex: "1 1 55%", minWidth: 220 }}>
          <FilePreview file={file} authorId={authorId} />
        </Box>
        <Box sx={{ flex: "1 1 34%", minWidth: 200 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
            <ViewsIcon sx={{ fontSize: 15, color: "#3730A3" }} />
            <Typography sx={{ fontSize: 12.5, fontWeight: 700 }}>
              Access history {accessList.length ? `(${accessList.length})` : ""}
            </Typography>
          </Box>
          {accessList.length === 0 ? (
            <Typography sx={{ fontSize: 12, color: "text.secondary" }}>Not accessed yet.</Typography>
          ) : (
            <Box sx={{ maxHeight: 320, overflowY: "auto", border: "1px solid #E8EAF6", borderRadius: 1.5, p: 0.75 }}>
              <Stack spacing={0.5}>
                {accessList.map((a) => {
                  const am = actionMeta(a.type);
                  const isDirect = a.type === "direct";
                  // "who": in-app events show the author; direct hits show the
                  // referrer host (or "public link" when opened straight).
                  const who = isDirect
                    ? (referrerHost(a.data?.referrer) || "public link")
                    : nameOf(a.authorId);
                  // "where": direct hits use CloudFront geo; in-app uses tz.
                  const where = isDirect
                    ? directWhere(a.data)
                    : (a.data?.tz ? String(a.data.tz).replace("_", " ") : "");
                  const campaign = isDirect ? String(a.data?.campaign?.campaign || "").trim() : "";
                  return (
                    <Box key={a.id} sx={{ display: "flex", alignItems: "center", gap: 0.75, fontSize: 12 }}>
                      <Box component="span" sx={{ ...contactCardSx.statusChip(am.bg, am.fg), flexShrink: 0 }}>{am.label}</Box>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#334155", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 110 }}>{who}</Typography>
                      <Typography sx={{ fontSize: 11.5, color: "text.secondary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {fmtWhen(a.timestamp)}{where ? ` · ${where}` : ""}
                      </Typography>
                      {campaign ? (
                        <Box component="span" sx={{ ...contactCardSx.statusChip("#E8EAF6", "#3F51B5"), flexShrink: 0 }}>{campaign}</Box>
                      ) : null}
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}
        </Box>
      </Box>
    </AddressbookPanelShell>
  );
};

/* ─────────────────────────── Page ─────────────────────────── */

const PageFiles = () => {
  const authorId = useSelector((state) => state.auth.userId);
  const user = useSelector((state) => state.auth.user);
  const userDomain = String(user?.domain || "").trim();
  const activeDomain = userDomain && userDomain !== "All" ? userDomain : "";

  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [scopeFilter, setScopeFilter] = useState("all"); // all | system | public
  const [selectedId, setSelectedId] = useState(null);
  const [accessLogs, setAccessLogs] = useState([]);
  const [accessLogHasMore, setAccessLogHasMore] = useState(false);
  const [accessLogLoading, setAccessLogLoading] = useState(false);
  const accessLogPageRef = useRef(1);
  const viewedRef = useRef(new Set()); // file ids already view-logged this session
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });
  const notify = useCallback((message, severity = "success") => setToast({ open: true, message, severity }), []);

  const { uploadSingle } = useUploadContext() || {};
  const fileInputRef = useRef(null);
  const [dragOver, setDragOver] = useState(false);
  const [pendingFile, setPendingFile] = useState(null); // File awaiting scope choice
  const [uploadScope, setUploadScope] = useState("public");
  const [uploadName, setUploadName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);

  const ACCESS_LOG_PAGE_SIZE = 50;
  const fetchAccessLogs = useCallback(async (page) => {
    const domainArg = activeDomain ? { domain: activeDomain } : {};
    setAccessLogLoading(true);
    try {
      const logRes = await apiGetBlocks({
        schemaType: SCHEMA_ACCESS,
        ...domainArg,
        ordering: { field: "timestamp", order: "Desc" },
        pagination: { page, limit: ACCESS_LOG_PAGE_SIZE },
      });
      const blocks = Array.isArray(logRes?.blocks) ? logRes.blocks : [];
      accessLogPageRef.current = page;
      setAccessLogs((prev) => {
        if (page <= 1) return blocks;
        const seen = new Set(prev.map((b) => b?.id).filter(Boolean));
        return [...prev, ...blocks.filter((b) => !b?.id || !seen.has(b.id))];
      });
      setAccessLogHasMore(blocks.length >= ACCESS_LOG_PAGE_SIZE);
    } catch (err) {
      console.error("Failed to load file access log:", err);
      if (page <= 1) setAccessLogs([]);
      setAccessLogHasMore(false);
    } finally {
      setAccessLogLoading(false);
    }
  }, [activeDomain]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const domainArg = activeDomain ? { domain: activeDomain } : {};
      const [systemRes, publicRes] = await Promise.all([
        apiGetFiles({ isSystem: true, ...domainArg }).catch(() => ({ files: [] })),
        apiGetFiles({ isPublic: true, ...domainArg }).catch(() => ({ files: [] })),
      ]);
      const byId = new Map();
      for (const f of [...(systemRes?.files || []), ...(publicRes?.files || [])]) {
        if (f && f.id && !byId.has(f.id)) byId.set(f.id, f);
      }
      const merged = [...byId.values()].sort(
        (a, b) => (Number(b.updatedAt) || Date.parse(b.updatedAt) || 0) - (Number(a.updatedAt) || Date.parse(a.updatedAt) || 0)
      );
      setFiles(merged);
      // Load the first page of the access log (blocks) alongside, newest first.
      await fetchAccessLogs(1);
    } catch (err) {
      console.error("Failed to load files:", err);
      notify("Could not load files", "error");
    } finally {
      setLoading(false);
    }
  }, [activeDomain, notify, fetchAccessLogs]);

  useEffect(() => { load(); }, [load]);

  const visibleFiles = useMemo(
    () => (scopeFilter === "all" ? files : files.filter((f) => scopeOf(f) === scopeFilter)),
    [files, scopeFilter]
  );

  const selected = useMemo(() => files.find((f) => f.id === selectedId) || null, [files, selectedId]);

  // Group access events per file id.
  const accessByFile = useMemo(() => {
    const map = {};
    for (const ev of accessLogs) {
      const fid = ev?.parentId;
      if (!fid) continue;
      if (!map[fid]) map[fid] = [];
      map[fid].push(ev);
    }
    return map;
  }, [accessLogs]);

  // Record an access event. "view" is logged once per file per session to
  // avoid noise from re-clicking cards; open/copy always log.
  const logAccess = useCallback((file, action) => {
    if (!file?.id || !authorId) return;
    if (action === "view") {
      if (viewedRef.current.has(file.id)) return;
      viewedRef.current.add(file.id);
    }
    const ev = {
      schemaType: SCHEMA_ACCESS,
      type: action,
      parentId: file.id,
      name: fileName(file),
      authorId,
      ...(activeDomain ? { domain: activeDomain } : {}),
      data: {
        tz: (() => { try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ""; } catch { return ""; } })(),
        locale: navigator?.language || "",
        ua: navigator?.userAgent || "",
      },
    };
    apiCreateBlock(ev)
      .then((block) => {
        if (block) setAccessLogs((prev) => [block, ...prev]);
      })
      .catch((err) => console.error("Failed to log file access:", err));
  }, [authorId, activeDomain]);

  // Generic clipboard copy + toast (used by the campaign-link builder).
  const copyText = async (text, message = "Copied") => {
    if (!text) return;
    try {
      await navigator.clipboard?.writeText(text);
      notify(message);
    } catch {
      notify("Could not copy", "error");
    }
  };

  const copyUrl = async (file) => {
    const url = publicUrlOf(file);
    if (!url) { notify("No public URL for this file", "error"); return; }
    try {
      await navigator.clipboard?.writeText(url);
      notify("Public URL copied");
      logAccess(file, "copy");
    } catch {
      notify("Could not copy URL", "error");
    }
  };

  // Open the scope picker for a chosen file (from + button or drag-drop).
  const pickFile = (file) => {
    if (!file) return;
    setPendingFile(file);
    setUploadName(file.name || "");
    setUploadScope(scopeFilter === "system" ? "system" : "public");
    setProgress(0);
  };

  const onInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) pickFile(file);
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) pickFile(file);
  };

  const doUpload = () => {
    const file = pendingFile;
    if (!file || !uploadSingle) return;
    if (!authorId) { notify("Missing author context. Please sign in again.", "error"); return; }
    const isSystem = uploadScope === "system";
    const isPublic = uploadScope === "public";
    const fileId = uuid_v4();
    const domainKey = String(userDomain || "myrighthand").trim() || "myrighthand";
    const ext = file.name && file.name.includes(".") ? file.name.split(".").pop().toLowerCase() : "";
    const publicObjectName = ext ? `${fileId}.${ext}` : fileId;
    const directory = isSystem ? `${domainKey}/system/files` : `${domainKey}`;
    const uploadKey = isPublic ? `${domainKey}/${publicObjectName}` : `${directory}/${fileId}`;
    const publicUrl = `${config?.URL?.REACT_APP_PUBLIC_ASSETS_BASE_URL}/${domainKey}/${publicObjectName}`;
    const privateUrl = `${config?.ENV?.REACT_APP_HLS_BASE_URL}/${directory}/${fileId}`;

    setUploading(true);
    setProgress(0);
    uploadSingle(
      directory,
      file,
      fileId,
      (p) => setProgress(p),
      async (err, _result, done) => {
        if (err) {
          setUploading(false);
          notify(err?.message || "Upload failed", "error");
          return;
        }
        try {
          const doc = await apiCreateFile({
            authorId,
            fileCategory: "Other",
            type: "Other",
            fileType: ext,
            mimeType: file.type || "application/octet-stream",
            url: isPublic ? publicUrl : privateUrl,
            publicUrl,
            key: uploadKey,
            size: String(file.size),
            description: String(uploadName || file.name || "").trim(),
            domain: userDomain || "",
            isSystem,
            ref: { id: "", collection: "" },
            status: "active",
            isPublic,
          });
          setUploading(false);
          setPendingFile(null);
          notify("File uploaded");
          await load();
          if (doc?.id) setSelectedId(doc.id);
        } catch (saveErr) {
          console.error("Failed to save file:", saveErr);
          setUploading(false);
          notify(saveErr?.message || "Upload failed to save", "error");
        }
        done && done();
      },
      { key: uploadKey, isPublic }
    );
  };

  const compactSelectSx = {
    width: 150,
    "& .MuiOutlinedInput-root": { height: 26, fontSize: 12, backgroundColor: "#fff" },
    "& .MuiSelect-select": { textAlign: "left", display: "flex", alignItems: "center" },
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "row", height: "100%", minHeight: 0, minWidth: 0, overflow: "hidden", gap: 1 }}>
      {/* List panel */}
      <ResizablePanel edge="right" defaultWidth={360} minWidth={280} maxWidth={520}>
        <AddressbookPanelShell
          titleContent={
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flex: 1, minWidth: 0 }}>
              <TextField select size="small" value={scopeFilter} onChange={(e) => setScopeFilter(e.target.value)} sx={compactSelectSx}>
                <MenuItem value="all" sx={{ fontSize: 12 }}>All Files</MenuItem>
                <MenuItem value="system" sx={{ fontSize: 12 }}>System</MenuItem>
                <MenuItem value="public" sx={{ fontSize: 12 }}>Public</MenuItem>
              </TextField>
            </Box>
          }
          headerActions={
            <IconButton
              size="small" onClick={() => fileInputRef.current?.click()} title="Add file"
              sx={{ backgroundColor: "#16a34a", "&:hover": { backgroundColor: "#15803d" }, width: 24, height: 24, p: 0 }}
            >
              <AddIcon sx={{ fontSize: 16, color: "#FFFFFF" }} />
            </IconButton>
          }
          footerLeft={`${visibleFiles.length} file${visibleFiles.length === 1 ? "" : "s"}`}
        >
          <input ref={fileInputRef} type="file" hidden onChange={onInputChange} />
          <Box
            onDragOver={(e) => { e.preventDefault(); if (!dragOver) setDragOver(true); }}
            onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
            onDrop={onDrop}
            sx={{
              minHeight: "100%",
              borderRadius: 2,
              border: dragOver ? "2px dashed #16a34a" : "2px dashed transparent",
              backgroundColor: dragOver ? "rgba(22,163,74,0.06)" : "transparent",
              transition: "background-color 0.15s, border-color 0.15s",
            }}
          >
            {loading ? (
              <Box sx={{ py: 4, display: "flex", justifyContent: "center" }}><CircularProgress size={20} /></Box>
            ) : visibleFiles.length === 0 ? (
              <Box sx={{ py: 5, textAlign: "center" }}>
                <UploadIcon sx={{ fontSize: 34, color: "#94A3B8" }} />
                <Typography sx={{ color: "text.secondary", fontSize: 13, mt: 0.5 }}>No files.</Typography>
                <Typography sx={{ color: "text.secondary", fontSize: 11 }}>Drag files here or use the + button.</Typography>
              </Box>
            ) : (
              <Stack spacing={1}>
                {visibleFiles.map((file) => (
                  <FileCard
                    key={file.id}
                    file={file}
                    selected={file.id === selectedId}
                    onSelect={(f) => { setSelectedId(f.id); logAccess(f, "view"); }}
                    onCopyUrl={copyUrl}
                    accessCount={(accessByFile[file.id] || []).length}
                  />
                ))}
              </Stack>
            )}
            {accessLogHasMore && (
              <Button
                size="small"
                onClick={() => fetchAccessLogs(accessLogPageRef.current + 1)}
                disabled={accessLogLoading}
                sx={{ mt: 1, textTransform: "none", fontSize: 12 }}
              >
                {accessLogLoading ? "Loading access history..." : "Load more access history"}
              </Button>
            )}
          </Box>
        </AddressbookPanelShell>
      </ResizablePanel>

      {/* Detail panel */}
      <ResizablePanel edge="right" defaultWidth={700} minWidth={300} maxWidth={700} fillRemaining>
        {selected ? (
          <FileDetailPanel
            key={selected.id}
            file={selected}
            authorId={authorId}
            onCopyUrl={copyUrl}
            onCopyText={copyText}
            onOpen={(f) => logAccess(f, "open")}
            accessList={accessByFile[selected.id] || []}
          />
        ) : (
          <AddressbookPanelShell title="">
            <Box sx={{ py: 6, textAlign: "center" }}>
              <Typography sx={{ color: "text.secondary", fontSize: 14 }}>Select a file to preview</Typography>
            </Box>
          </AddressbookPanelShell>
        )}
      </ResizablePanel>

      {/* Upload dialog: choose scope (System / Public) */}
      <Dialog open={Boolean(pendingFile)} onClose={() => { if (!uploading) setPendingFile(null); }} fullWidth maxWidth="sm">
        <DialogTitle>Add file</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FileIcon sx={{ color: "#94A3B8" }} />
              <Typography sx={{ fontSize: 13, wordBreak: "break-all" }}>
                {pendingFile?.name} {pendingFile ? `(${fmtSize(pendingFile.size)})` : ""}
              </Typography>
            </Box>
            <TextField label="Name" value={uploadName} onChange={(e) => setUploadName(e.target.value)} fullWidth size="small" />
            <TextField select label="Scope" value={uploadScope} onChange={(e) => setUploadScope(e.target.value)} fullWidth size="small">
              <MenuItem value="public">Public (shareable URL)</MenuItem>
              <MenuItem value="system">System</MenuItem>
            </TextField>
            {uploading ? (
              <Box>
                <LinearProgress variant="determinate" value={progress} />
                <Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>{progress}%</Typography>
              </Box>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingFile(null)} disabled={uploading}>Cancel</Button>
          <Button variant="contained" onClick={doUpload} disabled={uploading}>
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PageFiles;
